import React, { useCallback, useEffect, useState } from 'react';
import {
  getStatusPipeline,
  dispararPipeline,
  StatusPipeline,
} from '../../../api/comissionamentosFotoApi';
import { notify } from '../../../shared/feedback/notify';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import '../../../shared/feedback/aviso-de-tela.css';

/**
 * Processamento do repasse — estado das execuções e disparo manual
 * (comissionamentos/at-04, `BUG-006`).
 *
 * 🔴 Este cartão existe porque o cron das 06h **não roda sem instância viva**, e ficou 3
 * semanas parado sem que nada na tela dissesse isso: 21.198 boletos pagos (R$ 5,37 milhões)
 * ficaram fora do módulo e o único sintoma foi o número parar de crescer. É a camada 2 da
 * diretriz de crons (`.claude/12-crons-em-cloud-run.md`): **registrar cada execução** — sem
 * isso "não rodou" é invisível — e **um botão humano**, que é o que salva o dia em que a
 * infraestrutura falha.
 *
 * ⚠️ Não confundir com a **Sincronização de boletos do SGA**, o cartão vizinho: aquele traz
 * os boletos da Hinova para a base; este recalcula o repasse a partir do que já está na base.
 * Um não substitui o outro, e os dois precisam ter rodado para o número da tela estar certo.
 *
 * 🔴 Três estados distintos, como o carimbo da at-01: data do último SUCESSO (nunca da última
 * tentativa — execução que falhou não atualizou nada), "nunca executado" e "não foi possível
 * verificar". Colapsar os dois últimos faria a tela afirmar que o processamento nunca rodou
 * quando o backend só está fora.
 */

const formatar = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const ProcessamentoDoRepasse: React.FC = () => {
  const [status, setStatus] = useState<StatusPipeline | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [disparando, setDisparando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setStatus(await getStatusPipeline());
      setErro(null);
    } catch (e) {
      // Estado seguro é `erro`, nunca `status = null`: null seria lido como "nunca rodou".
      setStatus(null);
      setErro(
        e instanceof Error
          ? e.message
          : 'Não foi possível verificar o processamento do repasse.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const disparar = async () => {
    setConfirmando(false);
    setDisparando(true);
    try {
      const r = await dispararPipeline();
      if (r.disparado) {
        notify.success(
          'Processamento iniciado. Ele leva alguns minutos; acompanhe por esta tela.',
        );
      } else {
        // Não é erro: é o estado legítimo de já haver uma execução em curso.
        notify.warning('Já existe um processamento em andamento.', {
          toastId: 'pipeline-em-andamento',
        });
      }
      await carregar();
    } catch (e) {
      // Texto escrito à mão: "iniciar" não está entre os verbos que o helper conjuga, e
      // aqui a CONSEQUÊNCIA importa mais que o motivo HTTP (regra da at-65).
      notify.error(
        'Não foi possível iniciar o processamento do repasse. Tente de novo; se persistir, acione a TI.',
      );
    } finally {
      setDisparando(false);
    }
  };

  const ultimoSucesso = formatar(status?.ultimaExecucaoComSucesso ?? null);
  const ultimaTentativa = formatar(status?.ultimaTentativa ?? null);
  const falhouNaUltima =
    !!status && status.execucoes[0]?.status === 'FALHOU' && !status.emAndamento;
  // Terceiro estado: o registro de execuções ainda não existe neste ambiente. Não é "nunca
  // rodou" (o cron pode estar rodando sem deixar rastro) nem falha de consulta.
  const indisponivel = status?.disponivel === false;

  return (
    <div className="admin-card">
      <h3>Processamento do repasse</h3>
      <p className="admin-hint">
        Recalcula as regras sobre os boletos já importados e atualiza os valores de repasse.
        Acontece sozinho todo dia às 06h; use o botão para adiantar. É diferente da
        sincronização acima, que é quem traz os boletos do SGA.
      </p>

      {erro && (
        // A faixa não substitui o cartão: o botão continua disponível (BUG-V27).
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}

      <div
        style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginTop: 12 }}
      >
        <button
          type="button"
          className="admin-btn"
          onClick={() => setConfirmando(true)}
          disabled={disparando || status?.emAndamento || indisponivel}
        >
          {disparando ? 'Iniciando...' : 'Processar agora'}
        </button>
        <span style={{ fontSize: 13, color: 'var(--tema-texto-2, #5f6368)' }}>
          {carregando && !status && !erro && 'Verificando...'}
          {!carregando && !erro && indisponivel &&
            'O acompanhamento do processamento ainda não está configurado neste ambiente. O cálculo automático das 06h não depende desta tela e continua acontecendo.'}
          {!carregando && !erro && !indisponivel && ultimoSucesso && (
            <>
              Último processamento concluído em <strong>{ultimoSucesso}</strong>.
            </>
          )}
          {!carregando && !erro && !indisponivel && !ultimoSucesso &&
            'Nenhum processamento concluído ainda.'}
          {status?.emAndamento && ' Um processamento está em andamento agora.'}
        </span>
      </div>

      {/*
        A tentativa que falhou aparece SEPARADA do último sucesso: quem lê precisa saber que o
        dado é mais velho do que a data de "tentou agora" sugere (regra do carimbo da at-01).
      */}
      {falhouNaUltima && ultimaTentativa && (
        <p className="aviso-de-tela" style={{ marginTop: 12 }}>
          A tentativa de {ultimaTentativa} não foi concluída, então os valores continuam sendo
          os do processamento anterior.
          {status?.execucoes[0]?.erro ? ` Motivo: ${status.execucoes[0].erro}` : ''}
        </p>
      )}

      {/*
        🔴 Confirmação explicativa, no padrão do sistema (at-18/at-46: "toda ação nova avisa
        antes de executar, descrevendo o resultado"). Aqui ela não é formalidade: o
        processamento recalcula **todas as competências desde janeiro**, e o valor de repasse
        muda para o que a regra vigente produz hoje. Com o cálculo parado há semanas, a
        diferença entre o que está na tela e o que sairá daqui é grande — quem clica precisa
        saber disso antes, não depois.
      */}
      {confirmando && (
        <ConfirmModal
          isOpen
          title="Processar o repasse agora?"
          message={
            'O sistema vai reimportar os boletos e RECALCULAR todas as competências desde ' +
            'janeiro, com as regras como elas estão hoje. Os valores de repasse exibidos nas ' +
            'telas passam a ser o resultado desse novo cálculo — que pode diferir do que foi ' +
            'pago. Leva alguns minutos e não pode ser desfeito por aqui.'
          }
          confirmLabel="Processar agora"
          onConfirm={disparar}
          onCancel={() => setConfirmando(false)}
        />
      )}
    </div>
  );
};

export default ProcessamentoDoRepasse;
